import { useEffect, useRef, useState } from "react";

type FallbackState<T> = { key: string; data: T | null };

export function useClientFallback<T>(
  initial: T | null | undefined,
  key: string,
  fetcher: () => Promise<T | null>,
): { data: T | null; loading: boolean } {
  const [state, setState] = useState<FallbackState<T>>(() => ({
    key: initial ? key : "",
    data: initial ?? null,
  }));

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const resolved = state.key === key && key !== "" ? state : null;
  const data = initial ?? resolved?.data ?? null;
  const loading = !data && Boolean(key) && !resolved;

  useEffect(() => {
    if (!key || state.key === key) return;

    if (initial) {
      setState({ key, data: initial });
      return;
    }

    let active = true;
    fetcherRef
      .current()
      .then((result) => {
        if (active) setState({ key, data: result ?? null });
      })
      .catch(() => {
        if (active) setState({ key, data: null });
      });

    return () => {
      active = false;
    };
  }, [key, initial, state.key]);

  return { data, loading };
}
